/* eslint-disable max-lines */
// @ts-strict-ignore
import Link from 'next/link';
import styled from 'styled-components';
import { flatten } from 'lodash';
import i18n from 'src/utils/translate';
import { useSelector } from 'src/store/store';
import { trackFootfallEvent } from 'src/utils/footfall';
import { Event, Section as FFSection } from 'src/constants/footfall';
import { getEthnicityList, getGenderLabel } from 'src/utils/profileUtil';
import { formatDateString } from 'src/utils/date';
import HealthTags from 'src/components/healthTags';
import ProfileBio from 'src/modules/user/profile/components/profileBio';
import { PrimaryButton, LinkButton } from 'styles-js/buttons';
import { Section, SubTitleContainer, SubTitle, AttentionContainer } from 'src/modules/user/profile/components/shared/sharedStyles';
import { textLight, titleMedium } from 'styles-js/mixins/typography';
export default function ProfileAbout() {
  const {
    basics,
    conditions,
    interests,
    profileUser
  } = useSelector(({
    pages
  }) => pages.profile);
  const {
    age,
    gender,
    country,
    ethnicity,
    countryCode,
    percentageComplete
  } = basics;
  const {
    dateCreated,
    isOwnProfile
  } = profileUser;
  const createTreatmentTerms = treatments => {
    return treatments.map(item => ({
      id: item.entityId || item.id,
      name: item.entityName,
      slug: item.slug
    }));
  };
  const createConditionTerms = condition => {
    return {
      id: condition.entityId || condition.id,
      name: condition.entityName,
      slug: condition.slug
    };
  };
  const conditionsWithTreatments = flatten(conditions.map(c => [createConditionTerms(c), ...createTreatmentTerms(c.treatments)]));
  const terms = [...conditionsWithTreatments, ...interests];
  const filteredTerms = terms.reduce((acc, current) => {
    // remove duplicates
    if (!acc.find(term => term.id === current.id)) acc.push(current);
    return acc;
  }, []);

  // ethnicity is different ! now consistent with getEthnicityList().
  // This is a little different because in BG, we show the passed ethnicity if not found.
  // example on sanjeethhu's staging account - 'GB' and 'south-asian', which does not exist in our list.
  // this is probably from before when there were no limitations for country-ethnicity, or they were different.
  // the change will touch the default list (non-uk, non-us) - so e.g. NL, option would be 'WHITE', now shows 'WHITE caucasian'
  const getEthnicity = (countryCode, ethnicity) => {
    const ethnicityList = getEthnicityList(countryCode);
    const ethnicityLabel = ethnicityList.find(x => x.value === ethnicity)?.label;
    if (!ethnicityLabel) {
      return getEthnicityList().find(x => x.value === ethnicity).label;
    }
    return ethnicityLabel;
  };
  return <Container data-sentry-element="Container" data-sentry-component="ProfileAbout" data-sentry-source-file="profileAbout.tsx">
      {isOwnProfile && percentageComplete > 0 && <PercentContainer>
          <Percent>{Math.floor(percentageComplete)}{'% '}{i18n.t('Complete')}</Percent>
          <Link legacyBehavior passHref href="/profile/edit?popup=profile">
            <PrimaryButton as="a" onClick={() => trackFootfallEvent(Event.Clicked, {
          clickType: 'edit-profile',
          clickMetadata: {
            section: FFSection.ProfileAbout
          }
        })}>
              {i18n.t('Update')}
            </PrimaryButton>
          </Link>
        </PercentContainer>}
      {filteredTerms.length > 0 && <Section>
          <SubTitleContainer>
            <SubTitle>{i18n.t('Health Interests')}</SubTitle>
            {isOwnProfile && <Link legacyBehavior passHref href="/profile/edit#health-interest">
                <LinkButton as="a">{i18n.t('Edit')}</LinkButton>
              </Link>}
          </SubTitleContainer>
          <HealthTags tags={filteredTerms} onClick={(slug, id) => trackFootfallEvent(Event.Clicked, {
        clickType: `health-tag-id-${id}`,
        clickMetadata: {
          section: FFSection.ProfileAbout,
          element: slug,
          origin: isOwnProfile ? 'own-profile' : 'other-profile'
        }
      })} />
        </Section>}
      {isOwnProfile && filteredTerms.length === 0 && <Section>
          <SubTitleContainer>
            <SubTitle>{i18n.t('Health Interests')}</SubTitle>
          </SubTitleContainer>
          <AttentionContainer>
            {i18n.t('Get more relevant recommendations by ')}
            <Link href="/profile/edit" onClick={() => trackFootfallEvent(Event.Clicked, {
          clickType: 'edit-profile',
          clickMetadata: {
            section: FFSection.ProfileAbout,
            element: 'update-health-interests'
          }
        })}>
              {i18n.t('updating your health interests')}
            </Link>
            {i18n.t(' for an improved experience. You can make these public or private.')}
          </AttentionContainer>
        </Section>}

      <Section data-sentry-element="Section" data-sentry-source-file="profileAbout.tsx">
        <SubTitleContainer data-sentry-element="SubTitleContainer" data-sentry-source-file="profileAbout.tsx">
          <SubTitle data-sentry-element="SubTitle" data-sentry-source-file="profileAbout.tsx">{i18n.t('User Info')}</SubTitle>
          {isOwnProfile && <Link legacyBehavior passHref href="/profile/edit#basic-info">
              <LinkButton as="a">{i18n.t('Edit')}</LinkButton>
            </Link>}
        </SubTitleContainer>
        <DataGrid data-sentry-element="DataGrid" data-sentry-source-file="profileAbout.tsx">
          <span>
            <div>{i18n.t('Joined')}</div>
            <div data-testid="profile__about_joined">{formatDateString(dateCreated, 'MMMM dd, yyyy')}</div>
          </span>
          {age && <span>
              <div>{i18n.t('Age')}</div>
              <div data-testid="profile__about_age">{age}</div>
            </span>}
          {gender && <span>
              <div>{i18n.t('Gender')}</div>
              <div data-testid="profile__about_gender">{getGenderLabel(gender)}</div>
            </span>}
          {country && <span>
              <div>{i18n.t('Country')}</div>
              <div data-testid="profile__about_country">{country}</div>
            </span>}
          {ethnicity && <span>
              <div>{i18n.t('Ethnicity')}</div>
              <div data-testid="profile__about_ethnicity">{getEthnicity(countryCode, ethnicity)}</div>
            </span>}
        </DataGrid>
      </Section>

      <Section data-sentry-element="Section" data-sentry-source-file="profileAbout.tsx">
        <SubTitleContainer data-sentry-element="SubTitleContainer" data-sentry-source-file="profileAbout.tsx">
          <SubTitle data-sentry-element="SubTitle" data-sentry-source-file="profileAbout.tsx">{i18n.t('Bio')}</SubTitle>
          {isOwnProfile && <Link legacyBehavior passHref href="/profile/edit#bio">
              <LinkButton as="a">{i18n.t('Edit')}</LinkButton>
            </Link>}
        </SubTitleContainer>
        <ProfileBio data-sentry-element="ProfileBio" data-sentry-source-file="profileAbout.tsx" />
      </Section>
    </Container>;
}
const Container = styled.div`
  ${Section} {
    @media (${({
  theme
}) => theme.underScreenMedium}) {
      padding: 0 12px 12px;
    }
  }
`;
const PercentContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 30px;
  padding: 12px;
  background-color: ${({
  theme
}) => theme.colorBlueBackground};
`;
const Percent = styled.p`
  ${titleMedium};
`;
const DataGrid = styled.div`
  & span {
    display: grid;
    grid-template-columns: 2fr 80%;
    grid-gap: 10px;
    padding: 7px 0;
  }
  div:nth-child(even) {
    ${textLight};
  }
`;